import { z } from "zod";

import { campaignDisplayStatusSchema } from "./campaign-display-status";

/**
 * A channel a campaign sends on, derived from its step sequence (`emailNode` → email,
 * `linkedin*Node` → linkedin). A campaign may use both; a draft with no steps has none.
 */
export const campaignChannelSchema = z.enum(["email", "linkedin"]);

export type CampaignChannel = z.infer<typeof campaignChannelSchema>;

/**
 * Per-campaign summary row for the List Overview expandable sub-rows and the Campaigns
 * overview page (PREC-3880). Composes the derived display status (PREC-3249) with
 * engagement metrics. Metrics mirror the canonical definitions:
 * - `total`     — leads assigned to the campaign.
 * - `contacted` — leads with at least one conversation `sentAt` (Contacts `lastContactAt` semantics).
 * - `replied`   — leads with a real inbound reply (message_received / mail_received with a
 *                 sender); an out-of-office auto-reply is not a reply (PREC-3949).
 * - `positive`  — replied leads whose best conversation sentiment is INTERESTED.
 */
export const campaignSummarySchema = z.object({
	campaignId: z.string(),
	name: z.string(),
	isValid: z.boolean(),
	status: campaignDisplayStatusSchema,
	locked: z.boolean(),
	channels: z.array(campaignChannelSchema),
	total: z.number(),
	contacted: z.number(),
	replied: z.number(),
	positive: z.number(),
});

export type CampaignSummary = z.infer<typeof campaignSummarySchema>;

export const campaignSummaryResponseSchema = z.object({
	data: z.array(campaignSummarySchema),
});

export type CampaignSummaryResponse = z.infer<typeof campaignSummaryResponseSchema>;
